import { baseToApiShape, baseFromApiShape, baseInitNewForm, baseInitEditForm, withHeadOnlyPlaceholder } from '../formHelpers/base';
import { IMAGE_MODE } from '../../util/enums';
import { imageShapeCompatibility, parseImageName } from './imageCompat';

const EFA_INSTANCE_TYPES = [
    "m5dn.24xlarge", "m5dn.metal", "m5n.24xlarge", "m5n.metal", "m5zn.12xlarge", "m5zn.metal", "m6a.48xlarge", "m6a.metal", "m6i.32xlarge",
    "m6i.metal", "m6id.32xlarge", "m6id.metal", "m6idn.32xlarge", "m6idn.metal", "m6in.32xlarge", "m6in.metal", "m7g.16xlarge", "m7g.metal",
    "c5n.9xlarge", "c5n.18xlarge", "c5n.metal", "c6a.48xlarge", "c6a.metal", "c6gn.16xlarge", "c6i.32xlarge", "c6i.metal", "c6id.32xlarge",
    "c6id.metal", "c6in.32xlarge", "c6in.metal", "c7g.16xlarge", "c7g.metal", "c7gn.16xlarge", "hpc6a.48xlarge", "hpc7g.4xlarge", "hpc7g.8xlarge",
    "hpc7g.16xlarge", "hpc6id.32xlarge", "r5dn.24xlarge", "r5dn.metal", "r5n.24xlarge", "r5n.metal", "r6a.48xlarge", "r6a.metal", "r6i.32xlarge",
    "r6i.metal", "r6idn.32xlarge", "r6idn.metal", "r6in.32xlarge", "r6in.metal", "r6id.32xlarge", "r6id.metal", "r7g.16xlarge", "r7g.metal",
    "x2idn.32xlarge", "x2idn.metal", "x2iedn.32xlarge", "x2iedn.metal", "x2iezn.12xlarge", "x2iezn.metal", "i3en.12xlarge", "i3en.24xlarge",
    "i3en.metal", "i4g.16xlarge", "i4i.32xlarge", "i4i.metal", "im4gn.16xlarge", "dl1.24xlarge", "g4dn.8xlarge", "g4dn.12xlarge", "g4dn.16xlarge",
    "g4dn.metal", "g5.8xlarge", "g5.12xlarge", "g5.16xlarge", "g5.24xlarge", "g5.48xlarge", "g6.8xlarge", "g6.12xlarge", "g6.16xlarge",
    "g6.24xlarge", "g6.48xlarge", "g6e.8xlarge", "g6e.12xlarge", "g6e.16xlarge", "g6e.24xlarge", "g6e.48xlarge", "inf1.24xlarge", "p3dn.24xlarge",
    "p4d.24xlarge", "p4de.24xlarge", "p5.48xlarge", "trn1.32xlarge", "trn1n.32xlarge", "vt1.24xlarge", "c7gn.12xlarge",
];

/**
 * AWS-specific flat → nested API payload.
 * Extends base with: capacity auto-expire, marketplace AMIs, storage configurations, prefix,
 * and the unified image pin (AWS is unified-only).
 */
export function toApiShape(form) {
    const base = baseToApiShape(form);

    // Ensure EFA disabled for incompatible instances
    const nodeGroups = (form.nodeGroups || []).map(g => ({
        ...g,
        efa: isEfaCompatible(g.instance_id) ? (g.efa || false) : false,
    }));

    return {
        ...base,
        info: {
            ...base.info,
            imageMode: IMAGE_MODE.UNIFIED,
            // The exact image picked (a version or a cloud image); null when a stack was
            // picked and the deploy resolves its newest image.
            unifiedImageId: form.serverImageId || null,
            prefix: form.prefix || '',
            marketplace_head_image_id: form.marketplaceHeadImageId || null,
            marketplace_compute_image_id: form.marketplaceComputeImageId || null,
        },
        data: {
            ...base.data,
            storageConfigurations: form.storageConfigurations || [],
            network: {
                ...base.data.network,
                zone_name: form.zoneName || '',
                capacity_reservation_auto_expire: form.capacityReservationAutoExpire ?? true,
                capacity_reservation_expire_hours: form.capacityReservationExpireHours ?? 1,
            },
            node: {
                ...base.data.node,
                group: withHeadOnlyPlaceholder(nodeGroups, form),
            },
        },
    };
}

/** Image fields read back from a saved cluster: the pin lives in info.unifiedImageId. */
function savedImageFields(cluster) {
    return {
        imageMode: IMAGE_MODE.UNIFIED,
        serverImageId: cluster.info?.unifiedImageId || cluster.data?.server?.image_id || '',
        // Clusters saved before AWS became unified-only; the form says they will be converted.
        wasMultiImage: cluster.info?.imageMode === IMAGE_MODE.MULTI_IMAGE,
    };
}

export function fromApiShape(cluster) {
    return {
        ...baseFromApiShape(cluster),
        ...savedImageFields(cluster),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,

        zoneName: cluster.data?.network?.zone_name || '',
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,

        storageConfigurations: cluster.data?.storageConfigurations || [],
    };
}

export function initNewForm(provider, config) {
    return {
        ...baseInitNewForm(provider, config),
        imageMode: IMAGE_MODE.UNIFIED,
        zoneName: '',
        capacityReservationAutoExpire: true,
        capacityReservationExpireHours: 1,
        storageConfigurations: [],
        marketplaceHeadImageId: null,
        marketplaceComputeImageId: null,
    };
}

export function initEditForm(cluster, config) {
    return {
        ...baseInitEditForm(cluster, config),
        ...savedImageFields(cluster),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,
        zoneName: cluster.data?.network?.zone_name || '',
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,
        storageConfigurations: cluster.data?.storageConfigurations || [],
    };
}

/** Check if instance supports EFA — provider data first, then fallback list */
export function isEfaCompatible(instanceId, instanceSizes) {
    if (instanceSizes) {
        const inst = instanceSizes.find(s => s.instance_id === instanceId);
        if (inst?.EFA_support) return true;
    }
    return EFA_INSTANCE_TYPES.includes(instanceId);
}

/** Zone a compute group will actually land in: its own override, else the head node's zone. */
export function effectiveZone(group, form) {
    return (group && group.zone_id) || (form && form.zoneId) || '';
}

const groupProvisionsNodes = (g) => (g.count == null ? 1 : Number(g.count)) > 0;

/** Every instance type that will be placed in the head node's zone (head + groups without their own zone). */
export function headZoneInstanceIds(form) {
    const ids = [form?.serverInstanceId];
    (form?.nodeGroups || []).forEach((g) => {
        if (!g.zone_id && groupProvisionsNodes(g)) ids.push(g.instance_id);
    });
    return ids.filter(Boolean);
}

/**
 * Why the current AWS selection cannot be saved, or null when it can.
 *
 * The dropdowns already make a bad zone x shape combination unselectable; this
 * is the safety net for combinations that become invalid after the fact (an
 * edited cluster whose shape AWS has since withdrawn from that zone, a region
 * the credential no longer has, a head-zone change that breaks an inheriting
 * group). Only speaks when the live catalog is authoritative — silence on
 * "don't know", never a false block. The returned string fits the Save
 * tooltip's "Complete the following before saving: …" sentence.
 */
export function unofferedShapeReason(form, catalog) {
    if (!form || !catalog) return null;

    if (form.credentialId && form.regionId && catalog.regions.length && !catalog.regionsLoading && !catalog.regionsError
        && !catalog.regions.some((r) => r.region_id === form.regionId)) {
        return `a region this credential can use (${form.regionId} is not enabled for it)`;
    }
    if (!catalog.catalogReady) return null;

    const checks = [{ instance: form.serverInstanceId, zone: form.zoneId, role: 'the head node' }];
    (form.nodeGroups || []).forEach((g, i) => {
        if (groupProvisionsNodes(g) && g.instance_id) {
            checks.push({ instance: g.instance_id, zone: effectiveZone(g, form), role: `compute group ${i + 1}` });
        }
    });
    for (const c of checks) {
        if (!c.instance) continue;
        if (c.zone) {
            if (catalog.isOffered(c.zone, c.instance) === false) {
                return `a zone that offers ${c.instance} for ${c.role} (${catalog.zoneLabel(c.zone)} does not)`;
            }
        } else if (catalog.isOfferedInRegion(c.instance) === false) {
            return `an instance type offered in ${form.regionId} for ${c.role} (${c.instance} is offered in no zone)`;
        }
    }
    return null;
}

/** Roles that boot the unified image: the head node and every provisioning compute group. */
function imageRoles(form) {
    const roles = [{ role: 'head node', instance: form?.serverInstanceId }];
    (form?.nodeGroups || []).forEach((g, i) => {
        if (groupProvisionsNodes(g) && g.instance_id) roles.push({ role: `compute group ${i + 1}`, instance: g.instance_id });
    });
    return roles.filter((r) => r.instance);
}

/** Licence constraint for an image from its software-pricing entry (null = none known). */
export function licenceFor(software, productName) {
    if (!software || !software.subscribed || !software.licensedInstanceTypes || software.licensedInstanceTypes.length === 0) return null;
    return { licensedInstanceTypes: software.licensedInstanceTypes, productName: productName || software.productId || 'this Marketplace product' };
}

/**
 * Why a stack has no usable unified image for this credential and region.
 * `images` is the account's live image list for the chosen credential + region.
 */
export function stackUnavailableReason(stack, { images, credentialId, regionId, credentialsById = {} }) {
    const data = stack.data || {};
    if (data.region_id && data.region_id !== regionId) return `Built in ${data.region_id}`;
    if (stack.credential_id && stack.credential_id !== credentialId) {
        const name = credentialsById[stack.credential_id];
        return name ? `Built with credential ${name}` : 'Built with another credential';
    }
    const state = stack.info && stack.info.state;
    if (state === 'none' || !state) return 'Never built';
    if (state !== 'success') return `Build ${state}`;
    const legacyOnly = (images || []).some((img) => {
        const parsed = parseImageName(img.name);
        return parsed && parsed.prefix === data.prefix && parsed.kind !== 'heidi';
    });
    if (legacyOnly) return 'Multi-image (legacy) build: rebuild as unified';
    return 'Image deleted from AWS';
}

/**
 * Whether an image row can be picked: disabled, with the reason, when one of the
 * already-chosen shapes cannot boot it (or its licence does not cover them).
 * Silent until the image list for this credential + region has arrived.
 */
export function awsImageOptionState(image, { form, catalog, licence }) {
    if (!catalog || !catalog.imagesReady || !image) return { disabled: false, reason: null };
    for (const r of imageRoles(form)) {
        const verdict = imageShapeCompatibility(image, catalog.shapeById(r.instance), licence);
        if (!verdict.compatible) return { disabled: true, reason: `${verdict.imageReason} (${r.role})` };
    }
    return { disabled: false, reason: null };
}

/**
 * Whether a stack row can be picked: it needs a unified image in this account and
 * region, and that image has to boot the chosen shapes. `licenceOf(image)` gives the
 * image's Marketplace licence constraint, if any.
 */
export function awsStackOptionState(stack, { form, catalog, credentialsById, licenceOf }) {
    if (!catalog || !catalog.imagesReady) return { disabled: false, reason: null };
    const image = catalog.resolveImage(stack.data && stack.data.prefix, 'heidi');
    if (!image) {
        return {
            disabled: true,
            reason: stackUnavailableReason(stack, { images: catalog.images, credentialId: form?.credentialId, regionId: form?.regionId, credentialsById }),
        };
    }
    return awsImageOptionState(image, { form, catalog, licence: licenceOf ? licenceOf(image) : null });
}

/**
 * Why the current image choice cannot be saved, or null. Safety net for
 * states the dropdowns cannot prevent (edited clusters, an image deleted
 * since, a dry-run rejection arriving after a click). Phrased to fit the Save
 * tooltip's "Complete the following before saving: …" sentence.
 */
export function imageReason(form, { catalog, headImage, launchCheck, marketplace, marketplaceAmi, licence, software }) {
    if (!form || !catalog) return null;
    if (software && software.subscribed === false) {
        return `an active AWS Marketplace subscription for ${software.productName || 'this product'} (subscribe, then re-check)`;
    }
    if (marketplace && marketplace.isMarketStack && marketplace.config && marketplace.config.marketplace_product_code) {
        if (marketplaceAmi && !marketplaceAmi.loading && marketplaceAmi.region === form.regionId && marketplaceAmi.imageId === null) {
            return `a Marketplace image available in ${form.regionId} for this architecture`;
        }
    } else if (catalog.imagesReady && form.serverImageName) {
        if (form.serverImageId && !catalog.imageById(form.serverImageId)) {
            return 'an image that still exists (the selected one was deleted)';
        }
        if (!headImage) {
            return `an image available in ${form.regionId} (${form.serverImageName} has none there)`;
        }
    }
    if (headImage && catalog.catalogReady) {
        for (const r of imageRoles(form)) {
            const verdict = imageShapeCompatibility(headImage, catalog.shapeById(r.instance), licence);
            if (!verdict.compatible) return `an instance type the image can boot (${verdict.shapeReason} for ${r.role})`;
        }
    }
    if (launchCheck && launchCheck.rejected && launchCheck.rejected.length) {
        const first = launchCheck.rejected[0];
        return `a combination AWS accepts (${first.role}: ${first.message})`;
    }
    return null;
}

/** Check if any node groups are in different zones (EFA requires same zone) */
export function areZonesDifferent(form) {
    const headZone = form?.zoneId;
    const nodeZones = (form?.nodeGroups || []).map(g => g.zone_id || headZone).filter(Boolean);
    if (headZone) nodeZones.push(headZone);
    return new Set(nodeZones).size > 1;
}
